import { takeLatest, call, put, select } from "redux-saga/effects";
import _ from "lodash";
import { conditionallyCloseProject } from "../../../services/projects";
import { BACKEND_STATUS, STATUS_CODE } from "../../../utilities/statusUtils";
import { openSnackbar } from "../../_shared/snack-bar/snackbarActions";
import { errorOpenModal } from "../../_shared/modal/errorModalActions";
import * as actions from "../projectActions";
import { setData } from "../../projects/projectsActions";

const projectSelector = state => _.get(state, "projects.data");
const projectDetailsSelector = state => _.get(state, "projectDetails.data");

export default function* conditionallyCloseProjectWatcher() {
  yield takeLatest(actions.CONDITIONALLY_CLOSE, conditionallyCloseProjectWorker);
}

export function* conditionallyCloseProjectWorker(action) {
  // Validate closing_id before proceeding
  const closingId = _.get(action, 'closing_id');
  
  // Check for missing closing_id (undefined, null, or empty string) but allow 0 to pass through
  if (closingId === undefined || closingId === null || closingId === '') {
    const validationError = new Error('Invalid or missing closing_id');
    yield call(failure, validationError, { closing_id: 'missing' });
    return;
  }
  
  // Check if closing_id is a valid type (number or string)
  if (!_.isNumber(closingId) && !_.isString(closingId)) {
    const validationError = new Error('Invalid closing_id type');
    yield call(failure, validationError, { closing_id: closingId });
    return;
  }
  
  // Ensure closing_id is a valid number (convert string to number if needed)
  const normalizedClosingId = _.isNumber(closingId) ? closingId : parseInt(closingId, 10);
  
  if (_.isNaN(normalizedClosingId) || normalizedClosingId <= 0) {
    const validationError = new Error('Invalid closing_id value');
    yield call(failure, validationError, { closing_id: closingId });
    return;
  }
  
  const conditionalCloseRequest = {
    closing_id: normalizedClosingId,
  };
  
  try {
    const response = yield call(conditionallyCloseProject, conditionalCloseRequest);
    yield call(success, response, action);
  } catch (error) {
    yield call(failure, error, action);
  }
}

export function* success(response, action) {
  const conditionalCloseResult = _.get(response, "data.conditionallyCloseProject", {});
  
  // Handle missing or explicit failure response with defensive normalization
  // Accept boolean true, string "true" (case-insensitive), or string "1"
  const successValue = conditionalCloseResult.Success;
  const isSuccessful = 
    successValue === true || 
    successValue === 1 || 
    (typeof successValue === 'string' && 
     (successValue.toLowerCase() === 'true' || successValue === '1'));
  
  if (!isSuccessful) {
    // Use generic user-friendly message to avoid exposing backend implementation details
    yield put(
      errorOpenModal({
        title: "Project could not be conditionally closed",
        message: "The project could not be conditionally closed at this time. Please try again or contact support.",
      }),
    );
    return;
  }
  
  yield put(
    openSnackbar({
      message: "Project Conditionally Closed!",
      showCloseIcon: true,
    }),
  );

  if (action.projectOpen) {
    // Update the current project details in Redux
    let projectData = yield select(projectDetailsSelector);
    if (projectData && projectData.projectDetails) {
      yield put(actions.setData({
        ...projectData,
        projectDetails: {
          ...projectData.projectDetails,
          status_code: STATUS_CODE.CONDITIONALLY_CLOSED,
          status_code_desc: BACKEND_STATUS.CONDITIONALLY_CLOSED,
          desk_audit_closing_actual_date: null,
        },
      }));
    }
  } else {
    let projects = yield select(projectSelector);
    const targetProjectIndex = _.findIndex(projects, { closing_id: action.closing_id });

    // Handle case where project is not found
    if (targetProjectIndex === -1) {
      yield put(
        errorOpenModal({
          title: "Project not found",
          message: "The project could not be found. Please refresh the page and try again.",
        }),
      );
      return;
    }

    const updatedProject = { 
      ...projects[targetProjectIndex], 
      status_code: STATUS_CODE.CONDITIONALLY_CLOSED,
      status_code_desc: BACKEND_STATUS.CONDITIONALLY_CLOSED 
    };
    const updatedProjects = [updatedProject, ...projects.filter((_, i) => i !== targetProjectIndex)];
    
    yield put(setData(updatedProjects));
  }
}

export function* failure(error, action) {
  // Extract error details for better debugging
  const errorMessage = _.get(error, 'message', 'Unknown error');
  const errorStatus = _.get(error, 'response.status');
  const errorData = _.get(error, 'response.data');
  const closingId = _.get(action, 'closing_id', 'unknown');
  
  // Create enriched error object with context for debugging
  const enrichedError = {
    originalError: error,
    context: {
      operation: 'conditionallyCloseProject',
      closing_id: closingId,
      timestamp: new Date().toISOString(),
      errorMessage,
      errorStatus,
      errorData,
    },
  }; 
  
  yield put(actions.error(enrichedError));
  
  // Provide user-friendly error messages based on error type
  let userMessage = "An error occurred while setting the Project to Conditional Closure. Please try again.";
  
  if (errorStatus === 403 || errorStatus === 401) {
    userMessage = "You do not have permission to conditionally close this project. Please contact your administrator.";
  } else if (errorStatus === 404) {
    userMessage = `Project (ID: ${closingId}) was not found. It may have been deleted or you may not have access to it.`;
  } else if (errorStatus >= 500) {
    userMessage = "A server error occurred. Please try again later or contact support if the issue persists.";
  } else if (!errorStatus && errorMessage.toLowerCase().includes('network')) {
    userMessage = "Network error occurred. Please check your connection and try again.";
  }
  
  yield put(
    errorOpenModal({
      title: "Project could not be set to Conditional Closure",
      message: userMessage,
    }),
  );
  
  return enrichedError;
}
